// Notifications as the rows of the notifications list: what happened to
// one post (favourites, repeats, reactions) is one row, and so are the
// follows of one half day. Everything else is a row of its own.

const POST_TYPES = new Set(['like', 'repeat', 'pleroma:emoji_reaction'])
const FOLLOW_WINDOW = 12 * 3600 * 1000

// the key of the group a notification belongs to, or null
export const groupKey = (notification) => {
  const { type, status, created_at: createdAt } = notification
  if (POST_TYPES.has(type) && status?.id) return `post-${status.id}`
  if (type === 'follow') {
    const time = new Date(createdAt ?? Number.NaN).getTime()
    if (Number.isNaN(time)) return null
    return `follows-${Math.floor(time / FOLLOW_WINDOW)}`
  }
  return null
}

// [{ key, grouped, notifications }] in the order of the list given; a
// row sits where its first (newest) notification is. A group of one is a
// plain row, but keeps its group's key, so it stays the same row when it
// grows.
export const groupNotifications = (notifications) => {
  const rows = []
  const byKey = new Map()
  for (const notification of notifications) {
    const key = groupKey(notification)
    const row = key && byKey.get(key)
    if (row) {
      row.notifications.push(notification)
    } else {
      const next = {
        key: key ?? `n-${notification.id}`,
        notifications: [notification],
      }
      if (key) byKey.set(key, next)
      rows.push(next)
    }
  }
  return rows.map((row) => ({ ...row, grouped: row.notifications.length > 1 }))
}

// While the reader is scrolled down, rows keep the places they had
// (previousKeys, in order). A row that is new goes on top when it is newer
// than all of them (it just came in), else at the bottom (older ones
// loaded, a filter turned on).
export const stableOrder = (rows, previousKeys) => {
  if (!previousKeys) return rows
  const place = new Map(previousKeys.map((key, index) => [key, index]))
  const firstKept = rows.findIndex((row) => place.has(row.key))
  const fresh = rows.filter((row) => !place.has(row.key))
  const kept = rows
    .filter((row) => place.has(row.key))
    .sort((a, b) => place.get(a.key) - place.get(b.key))
  const newer = firstKept === -1 ? fresh : rows.slice(0, firstKept)
  const older = fresh.filter((row) => !newer.includes(row))
  return [...newer, ...kept, ...older]
}

// how many rows, for the unread badges: ten favourites on one post are
// one new thing
export const countRows = (notifications) =>
  groupNotifications(notifications).length
